/**
 * El cobro, con la interfaz de Stripe, dentro de nuestro panel.
 *
 * Es el componente incrustado `payment-details` de Connect: la misma ventana que
 * el vendedor vería en su panel de Stripe —importe, comisión, neto, tarjeta,
 * cronología y los botones de reembolsar y disputar—, servida por Stripe en un
 * iframe y firmada para su cuenta conectada.
 *
 * Por qué así y no reimplementándolo: estas ventas son cargos directos, así que
 * el vendedor es el comerciante legal y el reembolso es una decisión suya con
 * consecuencias legales y de dinero. Escribir aquí un botón de reembolsar sería
 * hacernos responsables de un flujo —parciales, disputas, plazos— que Stripe ya
 * mantiene. Esto trae ese flujo entero sin que nosotros escribamos su lógica, y
 * a cambio sólo cedemos el aspecto de esa ventana.
 *
 * Lo que NO se delega es lo que Stripe no sabe: qué oferta se vendió, que el
 * comprador es un contacto nuestro y qué otras mensualidades lleva pagadas. Eso
 * se queda en nuestra ficha, y esto se abre encima cuando hace falta el detalle
 * del cobro o hay que devolver el dinero.
 *
 * La instancia de Connect y los colores viven en `StripeEmbed`: son de las
 * cuatro vistas incrustadas, no de ésta.
 */
import { useI18n } from '../../../lib/i18n'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ConnectPaymentDetails } from '@stripe/react-connect-js'
import { EmbedLoading, StripeEmbedProvider, embedErrorMessage } from './StripeEmbed'

export { isEmbeddedStripeConfigured } from './StripeEmbed'

export function StripePaymentDetails({ projectId, token, paymentId, onClose }: {
  projectId: string
  token: string
  /** El PaymentIntent o el cargo, tal como lo conoce la cuenta conectada. */
  paymentId: string
  onClose: () => void
}) {
  const { t } = useI18n()
  const [failed, setFailed] = useState('')

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div
      className="dash-modal-backdrop dash-stripe-embed-backdrop"
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}
    >
      <div className="dash-stripe-embed" role="dialog" aria-modal="true" aria-label={t('stripe.embed.paymentAria')}>
        {failed ? (
          <p className="dash-order-detail-note">{failed}</p>
        ) : (
          <StripeEmbedProvider
            projectId={projectId}
            token={token}
            fallback={<EmbedLoading label={t('stripe.embed.openingPayment')} />}
            onFail={setFailed}
          >
            {/* El propio componente trae su aspa y su cabecera: cerrar desde
                dentro cierra también nuestra capa, para que no quede un fondo
                oscurecido sin nada encima. */}
            <ConnectPaymentDetails
              payment={paymentId}
              onClose={onClose}
              onLoadError={(event) => {
                console.error('[ventas] Stripe no cargó el cobro', event.error)
                setFailed(embedErrorMessage(event.error, 'stripe.embed.subject.payment', t))
              }}
            />
          </StripeEmbedProvider>
        )}
      </div>
    </div>,
    document.body,
  )
}
